/**
 * Callenzio – Vanilla-JS für cz-Sektionen (keine Libraries).
 * Scroll-Reveals · Parallax · Header-Glas · Announcement · Swatches/Crossfade ·
 * Monogramm-Konfigurator · AJAX-Add-to-Cart (öffnet den Atelier-Cart-Drawer) · Slider · Sticky-Leiste
 *
 * Tracking: Keine eigenen Tracking-Skripte. CTAs/Swatches/Konfigurator tragen data-cz-track-Attribute.
 * Add-to-Cart läuft über /cart/add.js → Shopify Customer Events (product_added_to_cart) für Meta/GA4.
 */

const html = document.documentElement;
const motion = () => html.classList.contains('cz-motion') && !matchMedia('(prefers-reduced-motion: reduce)').matches;
const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
/* Atelier scrollt ab 990px im .page-wrapper statt im Fenster – beide Quellen berücksichtigen. */
const scroller = () => document.querySelector('.page-wrapper');
const scrollTop = () => Math.max(window.scrollY, scroller()?.scrollTop || 0);
function onAnyScroll(fn) {
  window.addEventListener('scroll', fn, { passive: true });
  scroller()?.addEventListener('scroll', fn, { passive: true });
}
const routes = () => (window.Theme && window.Theme.routes) || { cart_add_url: '/cart/add', cart_url: '/cart' };

/* ------------------------------------------------------------------ Reveals */
let revealObserver;
function initReveals(root = document) {
  const items = $$('[data-cz-reveal], .cz-rule', root);
  if (!items.length) return;
  $$('[data-cz-stagger]', root).forEach((group) => {
    $$(':scope > [data-cz-reveal], :scope > * > [data-cz-reveal]', group).forEach((el, i) => el.style.setProperty('--cz-i', String(i % 6)));
  });
  if (!motion() || !('IntersectionObserver' in window)) {
    items.forEach((el) => el.classList.add('is-in'));
    return;
  }
  html.classList.add('cz-ready');
  revealObserver ||= new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          revealObserver.unobserve(e.target);
        }
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
  );
  items.forEach((el) => revealObserver.observe(el));
}

/* ----------------------------------------------------------------- Parallax */
const parallaxEls = new Set();
let parallaxTicking = false;
function updateParallax() {
  parallaxTicking = false;
  const vh = window.innerHeight;
  for (const el of parallaxEls) {
    const r = el.parentElement.getBoundingClientRect();
    if (r.bottom < 0 || r.top > vh) continue;
    const factor = parseFloat(el.dataset.czParallax) || 0.12;
    const offset = (r.top + r.height / 2 - vh / 2) * -factor;
    el.style.setProperty('--cz-parallax', `${offset.toFixed(1)}px`);
  }
}
function onScrollParallax() {
  if (!parallaxTicking) {
    parallaxTicking = true;
    requestAnimationFrame(updateParallax);
  }
}
function initParallax(root = document) {
  if (!motion()) return;
  $$('[data-cz-parallax]', root).forEach((el) => parallaxEls.add(el));
  if (parallaxEls.size) {
    onAnyScroll(onScrollParallax);
    window.addEventListener('resize', onScrollParallax, { passive: true });
    updateParallax();
  }
}

/* ------------------------------------------------------------ Header-Glas */
function initHeader() {
  const header = document.getElementById('header-component');
  if (!header) return;
  const set = () => header.classList.toggle('cz-scrolled', scrollTop() > 8);
  set();
  onAnyScroll(set);
}

/* ------------------------------------------------------------ Announcement */
function initAnnouncement(root = document) {
  $$('[data-cz-ann]', root).forEach((bar) => {
    if (bar.dataset.czReady) return;
    bar.dataset.czReady = '1';
    const items = $$('.cz-ann__item', bar);
    const toggle = $('.cz-ann__toggle', bar);
    if (items.length < 2) {
      toggle?.remove();
      return;
    }
    let i = 0;
    let timer = null;
    let paused = !motion();
    const speed = (parseInt(bar.dataset.czSpeed, 10) || 5) * 1000;
    const show = (n) => {
      items[i].classList.remove('is-active');
      items[i].setAttribute('aria-hidden', 'true');
      i = (n + items.length) % items.length;
      items[i].classList.add('is-active');
      items[i].removeAttribute('aria-hidden');
    };
    const start = () => {
      stop();
      if (!paused) timer = setInterval(() => show(i + 1), speed);
    };
    const stop = () => timer && clearInterval(timer);
    const render = () => {
      bar.classList.toggle('is-paused', paused);
      toggle?.setAttribute('aria-pressed', String(paused));
    };
    toggle?.addEventListener('click', () => {
      paused = !paused;
      render();
      start();
    });
    bar.addEventListener('mouseenter', stop);
    bar.addEventListener('mouseleave', start);
    bar.addEventListener('focusin', stop);
    bar.addEventListener('focusout', start);
    render();
    start();
  });
}

/* ------------------------------------------------------------ Hero-Video */
function initDeferredVideo(root = document) {
  $$('video[data-cz-src]', root).forEach((video) => {
    if (!motion() || navigator.connection?.saveData) return;
    const load = () => {
      video.src = video.dataset.czSrc;
      video.addEventListener('playing', () => video.classList.add('is-playing'), { once: true });
      video.play?.().catch(() => {});
    };
    if (document.readyState === 'complete') setTimeout(load, 300);
    else window.addEventListener('load', () => setTimeout(load, 300), { once: true });
  });
}

/* ------------------------------------------------------------ Hero-Video (cz-hero) */
if (!customElements.get('cz-hero')) {
  customElements.define(
    'cz-hero',
    class extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('.cz-hero__video');
        this.toggle = this.querySelector('.cz-hero__toggle');
        if (!this.video || this.dataset.czReady) return;
        this.dataset.czReady = '1';
        const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce || navigator.connection?.saveData) return;
        this.userPaused = false;
        const start = () => this.load();
        if (document.readyState === 'complete') {
          'requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 1500 }) : setTimeout(start, 200);
        } else {
          window.addEventListener('load', () => setTimeout(start, 150), { once: true });
        }
        this.toggle?.addEventListener('click', () => this.onToggle());
      }
      load() {
        const v = this.video;
        const mobile = matchMedia('(max-width: 749px)').matches;
        const src = (mobile && v.dataset.srcMobile) || v.dataset.src || v.dataset.srcMobile;
        if (!src) return;
        v.src = src;
        v.addEventListener('playing', () => {
          this.classList.add('is-playing');
          this.classList.remove('is-paused');
        });
        this.io = new IntersectionObserver(
          ([e]) => {
            this.inView = e.isIntersecting;
            this.sync();
          },
          { threshold: 0.05 }
        );
        this.io.observe(this);
        document.addEventListener('visibilitychange', () => this.sync());
      }
      sync() {
        const v = this.video;
        if (this.inView && !document.hidden && !this.userPaused) v.play().catch(() => {});
        else v.pause();
      }
      onToggle() {
        this.userPaused = !this.userPaused;
        this.toggle.setAttribute('aria-pressed', String(this.userPaused));
        this.toggle.setAttribute('aria-label', this.userPaused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
        this.classList.toggle('is-paused', this.userPaused);
        this.sync();
      }
      disconnectedCallback() {
        this.io?.disconnect();
      }
    }
  );
}

/* ------------------------------------------------------------ Produkte */
function readProduct(root) {
  const node = $('script[data-cz-product-json]', root);
  if (!node) return null;
  try {
    return JSON.parse(node.textContent);
  } catch {
    return null;
  }
}

function findVariant(product, selected) {
  return product.variants.find((v) => v.options.every((val, idx) => selected[idx] == null || selected[idx] === val));
}

function initProductUnits(root = document) {
  $$('[data-cz-product]', root).forEach((unit) => {
    if (unit.dataset.czReady) return;
    unit.dataset.czReady = '1';
    const product = readProduct(unit);
    if (!product) return;

    const form = $('form[data-cz-atc]', unit);
    const idInput = form && $('input[name="id"]', form);
    const selected = [];
    const current = product.variants.find((v) => String(v.id) === String(idInput?.value)) || product.variants[0];
    current?.options.forEach((val, idx) => (selected[idx] = val));

    const apply = (variant) => {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      // Bilder (Crossfade)
      const key = variant.image || product.image;
      $$('[data-cz-image]', unit).forEach((img) => img.classList.toggle('is-active', img.dataset.czImage === String(key)));
      // Preis
      $$('[data-cz-price]', unit).forEach((el) => (el.textContent = variant.price));
      // Farbname
      $$('[data-cz-color-name]', unit).forEach((el) => {
        const idx = parseInt(el.dataset.czColorName, 10);
        if (!Number.isNaN(idx)) el.textContent = variant.options[idx];
      });
      // Button-Zustand
      $$('[data-cz-submit]', unit).forEach((btn) => {
        btn.disabled = !variant.available;
        const label = $('.cz-btn__label', btn);
        if (label) label.textContent = variant.available ? btn.dataset.czLabel : btn.dataset.czSoldout;
      });
      // Sticky-Leiste synchronisieren
      $$('[data-cz-sticky-price]').forEach((el) => (el.textContent = variant.price));
      unit.dispatchEvent(new CustomEvent('cz:variant', { bubbles: true, detail: { variant, product } }));
    };

    $$('input[data-cz-option]', unit).forEach((input) => {
      input.addEventListener('change', () => {
        selected[parseInt(input.dataset.czOption, 10)] = input.value;
        apply(findVariant(product, selected));
      });
    });

    if (form) initAddToCart(form, unit);
  });
}

/* ------------------------------------------------------------ Monogramm */
function sanitizeInitials(value, max) {
  return value
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, max);
}

function initMonogram(root = document) {
  $$('[data-cz-monogram]', root).forEach((unit) => {
    if (unit.dataset.czMonoReady) return;
    unit.dataset.czMonoReady = '1';
    const input = $('[data-cz-initials-input]', unit);
    const previews = $$('[data-cz-initials]', unit);
    const preview = previews[0];
    const chip = $('[data-cz-chip-text]', unit);
    const error = $('[data-cz-initials-error]', unit);
    const hidden = $('[data-cz-initials-property]', unit);
    if (!input || !preview) return;
    const max = parseInt(input.maxLength, 10) > 0 ? parseInt(input.maxLength, 10) : 3;
    const sep = unit.dataset.czSeparator === 'dot' ? '.' : '';
    unit.czFormat = (v) => sanitizeInitials(v, max).split('').join(sep);
    let stampTimer;

    const update = () => {
      const clean = sanitizeInitials(input.value, max);
      if (input.value !== clean) {
        const invalid = /[^A-Za-z]/.test(input.value);
        input.value = clean;
        if (invalid && error) {
          error.textContent = error.dataset.czMsgChars;
          input.setAttribute('aria-invalid', 'true');
        }
      } else if (error && input.getAttribute('aria-invalid') === 'true') {
        error.textContent = '';
        input.removeAttribute('aria-invalid');
      }
      const display = clean.split('').join(sep);
      previews.forEach((p) => (p.textContent = display));
      if (hidden) hidden.value = display;
      if (chip) chip.textContent = clean ? `${chip.dataset.czPrefix} ${display}` : chip.dataset.czEmpty;
      clearTimeout(stampTimer);
      previews.forEach((p) => p.classList.remove('is-stamped'));
      if (clean && motion()) stampTimer = setTimeout(() => previews.forEach((p) => p.classList.add('is-stamped')), 20);
    };

    input.addEventListener('input', update);
    input.addEventListener('blur', update);
    update();

    unit.validate = () => {
      if (input.disabled) return true;
      const clean = sanitizeInitials(input.value, max);
      const required = unit.dataset.czRequired === 'true';
      if (required && !clean) {
        if (error) error.textContent = error.dataset.czMsgEmpty;
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        return false;
      }
      return true;
    };
  });
}

/* Vorschau-Swatches ohne Produkt: Farbname aktualisieren */
function initDemoSwatches(root = document) {
  $$('input[data-cz-demo-color]', root).forEach((input) => {
    input.addEventListener('change', () => {
      const fs = input.closest('fieldset');
      const name = fs && $('[data-cz-color-name]', fs);
      if (name) name.textContent = input.value;
    });
  });
}

/* ------------------------------------------------------------ Add to Cart */
let eventsModule;
async function getEvents() {
  if (eventsModule !== undefined) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch {
    eventsModule = null;
  }
  return eventsModule;
}

async function fetchCart() {
  const itemsComponent = document.querySelector('cart-items-component');
  if (itemsComponent?.fetchCartData) {
    await customElements.whenDefined('cart-items-component');
    return itemsComponent.fetchCartData();
  }
  const res = await fetch(`${routes().cart_url}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return res.json();
}

function initAddToCart(form, unit) {
  const status = $('[data-cz-status]', unit);
  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const mono = form.closest('[data-cz-monogram]');
    if (mono?.validate && !mono.validate()) return;

    const button = $('[data-cz-submit]', form);
    const fd = new FormData(form);
    const id = fd.get('id');
    if (!id) return;
    const properties = {};
    for (const [k, v] of fd.entries()) {
      const m = /^properties\[(.+)\]$/.exec(k);
      if (m && String(v).trim() !== '') properties[m[1]] = String(v);
    }
    if (mono?.czFormat && mono.dataset.czProp && properties[mono.dataset.czProp]) {
      properties[mono.dataset.czProp] = mono.czFormat(properties[mono.dataset.czProp]);
    }
    const quantity = parseInt(fd.get('quantity'), 10) || 1;

    button?.setAttribute('aria-busy', 'true');
    if (status) status.textContent = '';

    const events = await getEvents();
    let deferred = null;
    if (events?.CartLinesUpdateEvent) {
      deferred = events.CartLinesUpdateEvent.createPromise();
      form.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(id), quantity }],
          promise: deferred.promise,
        })
      );
    }

    try {
      const res = await fetch(`${routes().cart_add_url}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity, properties }] }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const msg = data.description || data.message || 'Fehler';
        if (status) status.textContent = msg;
        if (events?.CartErrorEvent) form.dispatchEvent(new events.CartErrorEvent({ error: msg, code: 'INVALID' }));
        const cart = await fetchCart().catch(() => null);
        deferred?.resolve({
          cart: cart ? events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) : undefined,
          detail: { didError: true, items: cart?.items, source: 'cz' },
        });
        return;
      }
      const cart = await fetchCart();
      deferred?.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'cz', itemCount: quantity, didError: false },
      });
      if (status) status.textContent = status.dataset.czAdded || '';
      if (!events) window.location.href = routes().cart_url;
    } catch (err) {
      deferred?.reject(err);
      if (status) status.textContent = status.dataset.czError || 'Fehler';
    } finally {
      button?.removeAttribute('aria-busy');
    }
  });
}

/* ------------------------------------------------------------ Slider */
function initSliders(root = document) {
  $$('[data-cz-slider]', root).forEach((slider) => {
    if (slider.dataset.czReady) return;
    slider.dataset.czReady = '1';
    const track = $('[data-cz-track]', slider);
    if (!track) return;
    const step = () => (track.firstElementChild?.getBoundingClientRect().width || track.clientWidth) + 16;
    $('[data-cz-prev]', slider)?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: motion() ? 'smooth' : 'auto' }));
    $('[data-cz-next]', slider)?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: motion() ? 'smooth' : 'auto' }));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        track.scrollBy({ left: step() });
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        track.scrollBy({ left: -step() });
      }
    });
  });
}

/* ------------------------------------------------------------ Sticky-Leiste */
function initSticky() {
  const bar = $('[data-cz-sticky]');
  if (!bar || !('IntersectionObserver' in window)) return;
  const trigger = $('[data-cz-hero-cta]');
  const hideZones = $$('[data-cz-sticky-hide]');
  let pastHero = false;
  let inHideZone = 0;
  const render = () => {
    const visible = pastHero && inHideZone === 0;
    bar.classList.toggle('is-visible', visible);
    bar.toggleAttribute('inert', !visible);
  };
  if (trigger) {
    new IntersectionObserver(([e]) => {
      pastHero = !e.isIntersecting && e.boundingClientRect.top < 0;
      render();
    }).observe(trigger);
  } else {
    pastHero = true;
  }
  const zoneObs = new IntersectionObserver((entries) => {
    for (const e of entries) {
      const was = e.target.dataset.czIn === '1';
      if (e.isIntersecting && !was) {
        e.target.dataset.czIn = '1';
        inHideZone++;
      } else if (!e.isIntersecting && was) {
        e.target.dataset.czIn = '0';
        inHideZone--;
      }
    }
    render();
  });
  hideZones.forEach((z) => zoneObs.observe(z));
  render();
}

/* ------------------------------------------------------------ Produktseite: Galerie */
function initGallery(root = document) {
  $$('[data-cz-gallery]', root).forEach((gal) => {
    if (gal.dataset.czReady) return;
    gal.dataset.czReady = '1';
    const unit = gal.closest('[data-cz-pdp]') || document;
    const track = $('[data-cz-gal-track]', gal);
    const bar = $('[data-cz-gal-bar]', gal);
    const count = $('[data-cz-gal-count]', gal);
    const prev = $('[data-cz-gal-prev]', gal);
    const next = $('[data-cz-gal-next]', gal);
    const thumbs = $('[data-cz-gal-thumbs]', unit);
    const all = $$('[data-cz-slide]', track);
    const visible = () => all.filter((el) => !el.hidden);
    let index = 0;

    const render = () => {
      const list = visible();
      const n = Math.max(list.length, 1);
      bar?.style.setProperty('--cz-seg', `${100 / n}%`);
      bar?.style.setProperty('--cz-pos', `${index * 100}%`);
      if (count) count.textContent = `${index + 1} ${count.dataset.czOf || '/'} ${n}`;
      if (prev) prev.disabled = index <= 0;
      if (next) next.disabled = index >= n - 1;
      const currentId = list[index]?.dataset.czMediaId;
      $$('[data-cz-thumb]', thumbs || gal).forEach((t) => t.setAttribute('aria-current', String(t.dataset.czThumb === currentId)));
      list.forEach((el, i) => el.toggleAttribute('inert', i !== index));
    };

    const goTo = (i, smooth = true) => {
      const list = visible();
      index = Math.max(0, Math.min(i, list.length - 1));
      track.scrollTo({ left: index * track.clientWidth, behavior: smooth && motion() ? 'smooth' : 'auto' });
      render();
    };

    let raf = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / Math.max(track.clientWidth, 1));
          if (i !== index) {
            index = i;
            render();
          }
        });
      },
      { passive: true }
    );
    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); goTo(index + 1, false); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); goTo(index - 1, false); }
    });
    thumbs?.addEventListener('click', (e) => {
      const t = e.target.closest('[data-cz-thumb]');
      if (!t) return;
      const i = visible().findIndex((el) => el.dataset.czMediaId === t.dataset.czThumb);
      if (i >= 0) goTo(i, false);
    });
    window.addEventListener('resize', () => goTo(index, false), { passive: true });

    /* Farbwechsel: passende Bildgruppe, Crossfade (kurz ausblenden, tauschen, einblenden) */
    const showColor = (color) => {
      const has = all.some((el) => el.dataset.czColor === color);
      const key = has ? color : null;
      const swap = () => {
        all.forEach((el) => (el.hidden = !(el.dataset.czColor === 'alle' || !key || el.dataset.czColor === key)));
        if (thumbs) {
          $$('[data-cz-thumb-for]', thumbs).forEach((li) => {
            const slide = all.find((el) => el.dataset.czMediaId === li.dataset.czThumbFor);
            li.hidden = !!slide?.hidden;
          });
        }
        index = 0;
        track.scrollLeft = 0;
        render();
        // Mini-Vorschau im Formular mit der Frontansicht der Farbe synchronisieren
        const front = visible().find((el) => el.hasAttribute('data-cz-front')) || visible()[0];
        const src = front && $('img', front);
        const mini = $('[data-cz-mini] img', unit);
        if (src && mini) {
          mini.srcset = src.srcset;
          mini.src = src.currentSrc || src.src;
        }
      };
      clearTimeout(gal.czSwitchTimer);
      if (!motion()) return swap();
      gal.classList.add('is-switching');
      gal.czSwitchTimer = setTimeout(() => {
        swap();
        requestAnimationFrame(() => gal.classList.remove('is-switching'));
      }, 150);
    };
    gal.czShowColor = showColor;
    gal.czGoFront = () => {
      const i = visible().findIndex((el) => el.hasAttribute('data-cz-front'));
      if (i >= 0 && i !== index) goTo(i);
    };

    unit.addEventListener('cz:variant', (e) => {
      const { variant, product } = e.detail;
      const colorIdx = $('[data-cz-color-name]', unit)?.dataset.czColorName;
      const val = variant.options[parseInt(colorIdx, 10)];
      if (val == null) return;
      const handle = val.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const umlaut = val.toLowerCase().replace(/ä/g, 'a').replace(/ö/g, 'o').replace(/ü/g, 'u').replace(/ß/g, 'ss').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const color = all.some((el) => el.dataset.czColor === umlaut) ? umlaut : handle;
      if (gal.dataset.czActiveColor !== color) {
        gal.dataset.czActiveColor = color;
        showColor(color);
      }
      void product;
    });

    initZoom(gal, unit, visible, () => index, goTo);
    render();
  });
}

/* ------------------------------------------------------------ Produktseite: Zoom-Dialog */
function initZoom(gal, unit, visible, getIndex, goTo) {
  const dialog = $('[data-cz-zoom]', unit);
  if (!dialog || typeof dialog.showModal !== 'function') return;
  const img = $('[data-cz-zoom-img]', dialog);
  const stage = $('[data-cz-zoom-stage]', dialog);
  const counter = $('[data-cz-zoom-count]', dialog);
  let i = 0;
  let opener = null;

  const show = (n) => {
    const list = visible();
    i = (n + list.length) % list.length;
    const slide = list[i];
    const thumb = $('img', slide);
    dialog.classList.remove('is-zoomed');
    img.src = slide.dataset.czZoomSrc || thumb?.currentSrc || thumb?.src || '';
    img.alt = thumb?.alt || '';
    if (counter) counter.textContent = `${i + 1} / ${list.length}`;
  };
  $$('[data-cz-zoom-open]', gal).forEach((btn) =>
    btn.addEventListener('click', () => {
      opener = btn;
      show(visible().indexOf(btn.closest('[data-cz-slide]')));
      dialog.showModal();
    })
  );
  $('[data-cz-zoom-prev]', dialog)?.addEventListener('click', () => show(i - 1));
  $('[data-cz-zoom-next]', dialog)?.addEventListener('click', () => show(i + 1));
  $('[data-cz-zoom-close]', dialog)?.addEventListener('click', () => dialog.close());
  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowRight') show(i + 1);
    else if (e.key === 'ArrowLeft') show(i - 1);
  });
  dialog.addEventListener('close', () => {
    if (i !== getIndex()) goTo(i, false);
    opener?.focus();
  });
  /* Klick/Tippen: 2,2-fach an der angetippten Stelle; Zeiger bewegt den Ausschnitt */
  stage.addEventListener('click', (e) => {
    if (e.target !== img) return dialog.classList.contains('is-zoomed') ? dialog.classList.remove('is-zoomed') : dialog.close();
    const r = img.getBoundingClientRect();
    img.style.setProperty('--cz-zx', `${((e.clientX - r.left) / r.width) * 100}%`);
    img.style.setProperty('--cz-zy', `${((e.clientY - r.top) / r.height) * 100}%`);
    dialog.classList.toggle('is-zoomed');
  });
  stage.addEventListener('pointermove', (e) => {
    if (!dialog.classList.contains('is-zoomed') || e.pointerType !== 'mouse') return;
    const r = stage.getBoundingClientRect();
    img.style.setProperty('--cz-zx', `${((e.clientX - r.left) / r.width) * 100}%`);
    img.style.setProperty('--cz-zy', `${((e.clientY - r.top) / r.height) * 100}%`);
  });
  /* Wischen im Dialog (nur ungezoomt) */
  let sx = null;
  stage.addEventListener('pointerdown', (e) => { if (!dialog.classList.contains('is-zoomed')) sx = e.clientX; });
  stage.addEventListener('pointerup', (e) => {
    if (sx == null) return;
    const dx = e.clientX - sx;
    sx = null;
    if (Math.abs(dx) > 48) show(i + (dx < 0 ? 1 : -1));
  });
}

/* ------------------------------------------------------------ Produktseite: Mit / ohne Prägung */
function initPersonal(root = document) {
  $$('[data-cz-pdp]', root).forEach((unit) => {
    if (unit.dataset.czPersonalReady) return;
    unit.dataset.czPersonalReady = '1';
    const field = $('[data-cz-initials-field]', unit);
    const input = $('[data-cz-initials-input]', unit);
    const error = $('[data-cz-initials-error]', unit);
    const chip = $('.cz-gal__chip', unit);
    const radios = $$('[data-cz-personal-choice]', unit);
    if (!radios.length) {
      // Personalisierung deaktiviert (Ware ab Lager): nichts ist Pflicht
      unit.dataset.czChoice = 'none';
      unit.dataset.czRequired = 'false';
      return;
    }
    const set = (value) => {
      const mono = value !== 'none';
      unit.dataset.czChoice = mono ? 'mono' : 'none';
      unit.dataset.czRequired = String(mono);
      if (field) field.hidden = !mono;
      if (chip) chip.hidden = !mono;
      if (input) {
        input.disabled = !mono;
        input.required = mono;
        if (!mono) {
          input.removeAttribute('aria-invalid');
          if (error) error.textContent = '';
        }
      }
      $$('[data-cz-initials]', unit).forEach((p) => (p.style.visibility = mono ? '' : 'hidden'));
    };
    $$('[data-cz-personal-choice]', unit).forEach((r) => r.addEventListener('change', () => r.checked && set(r.value)));
    set($('[data-cz-personal-choice]:checked', unit)?.value || 'mono');
    // Beim Tippen die Frontansicht zeigen, damit die Vorschau sichtbar ist
    input?.addEventListener('focus', () => $('[data-cz-gallery]', unit)?.czGoFront?.());
  });
}

/* ------------------------------------------------------------ Produktseite: Kaufleiste */
function initPdpSticky() {
  const bar = $('[data-cz-pdp-sticky]');
  const unit = $('[data-cz-pdp]');
  if (!bar || !unit || !('IntersectionObserver' in window)) return;
  const atc = $('[data-cz-pdp-atc]', unit);
  const form = $('form[data-cz-atc]', unit);
  const input = $('[data-cz-initials-input]', unit);
  const action = $('[data-cz-sticky-action]', bar);
  const label = action && $('.cz-btn__label', action);
  const colorEl = $('[data-cz-sticky-color]', bar);
  let atcVisible = true;
  let scrolled = false;

  const ready = () => unit.dataset.czChoice === 'none' || !!(input && input.value.trim());
  const relabel = () => {
    if (!label) return;
    const next = ready() ? action.dataset.czLabelBuy : action.dataset.czLabelMono;
    if (label.textContent === next) return;
    if (!motion()) {
      label.textContent = next;
      return;
    }
    clearTimeout(action.czRelabelTimer);
    action.classList.add('is-relabel');
    action.czRelabelTimer = setTimeout(() => {
      label.textContent = next;
      action.classList.remove('is-relabel');
    }, 140);
  };
  const render = () => {
    const visible = !atcVisible && scrolled;
    bar.classList.toggle('is-visible', visible);
    bar.toggleAttribute('inert', !visible);
  };
  new IntersectionObserver(([e]) => {
    atcVisible = e.isIntersecting;
    render();
  }).observe(atc || form);
  onAnyScroll(() => {
    const s = scrollTop() > 160;
    if (s !== scrolled) {
      scrolled = s;
      render();
    }
  });

  action?.addEventListener('click', () => {
    if (ready()) {
      if (form.requestSubmit) form.requestSubmit();
      else form.dispatchEvent(new Event('submit', { cancelable: true }));
      return;
    }
    input.scrollIntoView({ behavior: motion() ? 'smooth' : 'auto', block: 'center' });
    setTimeout(() => input.focus({ preventScroll: true }), motion() ? 450 : 0);
  });
  input?.addEventListener('input', relabel);
  $$('[data-cz-personal-choice]', unit).forEach((r) => r.addEventListener('change', relabel));
  unit.addEventListener('cz:variant', (e) => {
    const { variant } = e.detail;
    const idx = parseInt($('[data-cz-color-name]', unit)?.dataset.czColorName, 10);
    if (colorEl && !Number.isNaN(idx)) colorEl.textContent = variant.options[idx];
    if (action) action.disabled = !variant.available;
  });
  relabel();
}

/* ------------------------------------------------------------ Produktseite: Farbkacheln */
function initColorway(root = document) {
  $$('[data-cz-colorway]', root).forEach((sec) => {
    if (sec.dataset.czReady) return;
    sec.dataset.czReady = '1';
    sec.addEventListener('click', (e) => {
      const card = e.target.closest('[data-cz-pick-color]');
      const unit = $('[data-cz-pdp]');
      if (!card || !unit) return;
      e.preventDefault();
      const color = card.dataset.czPickColor;
      const input = $$('input[data-cz-option]', unit).find((i) => i.value === color);
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
      unit.scrollIntoView({ behavior: motion() ? 'smooth' : 'auto', block: 'start' });
    });
  });
}

/* ------------------------------------------------------------ Motion: Wort-Masken */
/* Überschriften mit data-cz-split: jedes Wort steigt aus einer Maske auf (Reveal über .is-in).
   data-cz-split="scrub": Worte leuchten nacheinander auf, gesteuert vom Scrollen (--cz-p aus initScrub).
   Screenreader lesen den Text unverändert (nur Inline-Spans, Leerzeichen bleiben erhalten). */
function initSplit(root = document) {
  $$('[data-cz-split]', root).forEach((el) => {
    if (el.dataset.czSplitReady) return;
    el.dataset.czSplitReady = '1';
    if (!motion()) {
      el.classList.add('is-split', 'is-in');
      return;
    }
    let k = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((n) => {
        if (n.nodeType === 3) {
          const frag = document.createDocumentFragment();
          n.textContent.split(/(\s+)/).forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) return void frag.append(part);
            const w = document.createElement('span');
            w.className = 'cz-w';
            const i = document.createElement('span');
            i.className = 'cz-w__i';
            i.style.setProperty('--cz-k', String(k++));
            i.textContent = part;
            w.append(i);
            frag.append(w);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') {
          walk(n);
        }
      });
    };
    walk(el);
    el.style.setProperty('--cz-n', String(k));
    el.classList.add('is-split');
  });
}

/* ------------------------------------------------------------ Motion: Scroll-Fortschritt */
/* Elemente mit data-cz-scrub erhalten --cz-p (0 → 1), während sie von data-cz-scrub-start (Anteil der
   Fensterhöhe, Standard 0.9) bis data-cz-scrub-end (Standard 0.35) wandern. Ein rAF pro Scroll-Frame,
   nur für sichtbare Elemente. */
const scrubEls = new Set();
let scrubTicking = false;
function updateScrub() {
  scrubTicking = false;
  const vh = window.innerHeight;
  for (const el of scrubEls) {
    const r = el.getBoundingClientRect();
    if (r.bottom < -vh * 0.25 || r.top > vh * 1.25) continue;
    const start = vh * (parseFloat(el.dataset.czScrubStart) || 0.9);
    const endAttr = parseFloat(el.dataset.czScrubEnd);
    const end = vh * (Number.isNaN(endAttr) ? 0.35 : endAttr);
    const p = Math.min(1, Math.max(0, (start - r.top) / Math.max(start - end, 1)));
    el.style.setProperty('--cz-p', p.toFixed(3));
  }
}
function requestScrub() {
  if (!scrubTicking) {
    scrubTicking = true;
    requestAnimationFrame(updateScrub);
  }
}
let scrubBound = false;
function initScrub(root = document) {
  const els = $$('[data-cz-scrub]', root);
  if (!els.length) return;
  if (!motion()) {
    els.forEach((el) => el.style.setProperty('--cz-p', '1'));
    return;
  }
  els.forEach((el) => scrubEls.add(el));
  if (!scrubBound) {
    scrubBound = true;
    onAnyScroll(requestScrub);
    window.addEventListener('resize', requestScrub, { passive: true });
  }
  updateScrub();
}

/* ------------------------------------------------------------ Motion: Goldlinie für Wischreihen */
function initTrackProgress(root = document) {
  $$('[data-cz-track-progress]', root).forEach((track) => {
    if (track.dataset.czTpReady) return;
    track.dataset.czTpReady = '1';
    const bar = $('[data-cz-track-bar]', track.parentElement);
    if (!bar) return;
    let raf = 0;
    const set = () => {
      const max = track.scrollWidth - track.clientWidth;
      const vis = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
      bar.parentElement.hidden = max < 4;
      bar.style.setProperty('--cz-sw', `${(vis * 100).toFixed(1)}%`);
      bar.style.setProperty('--cz-sx', `${max > 0 ? ((track.scrollLeft / max) * (1 / vis - 1) * 100).toFixed(1) : 0}%`);
    };
    track.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(set);
    }, { passive: true });
    window.addEventListener('resize', set, { passive: true });
    set();
  });
}

/* ------------------------------------------------------------ Social Proof */
function initProof(root = document) {
  $$('[data-cz-proof]', root).forEach((section) => {
    if (window.Shopify?.designMode) return;
    const check = () => {
      const apps = $('[data-cz-proof-apps]', section);
      const hasUgc = !!$('.cz-ugc__item', section);
      const appsEmpty = !apps || apps.getBoundingClientRect().height < 8;
      section.closest('.shopify-section')?.toggleAttribute('hidden', appsEmpty && !hasUgc);
    };
    window.addEventListener('load', () => setTimeout(check, 1500), { once: true });
  });
}

/* ------------------------------------------------------------ Init */
function init(root = document) {
  initSplit(root);
  initReveals(root);
  initScrub(root);
  initTrackProgress(root);
  initParallax(root);
  initAnnouncement(root);
  initDeferredVideo(root);
  initMonogram(root);
  initGallery(root);
  initPersonal(root);
  initColorway(root);
  initProductUnits(root);
  initSliders(root);
  initProof(root);
  initDemoSwatches(root);
}

/* Footer: „Cookie-Einstellungen“ öffnet Shopifys Einwilligungsbanner (nur sichtbar, wenn vorhanden) */
function initCookiePrefs() {
  const btn = $('[data-cz-cookie-prefs]');
  if (!btn) return;
  const show = () => {
    if (window.privacyBanner?.showPreferences) btn.hidden = false;
  };
  show();
  window.addEventListener('load', () => setTimeout(show, 1200), { once: true });
  btn.addEventListener('click', () => window.privacyBanner?.showPreferences?.());
}

initHeader();
init();
initCookiePrefs();
initSticky();
initPdpSticky();

// Theme-Editor: Sektionen neu initialisieren
document.addEventListener('shopify:section:load', (e) => init(e.target));
